import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import RefreshIcon from '@mui/icons-material/Refresh'

import { useLlmUsage, useLlmUsageDistinct } from '../../hooks/useLlmUsage'
import { FEATURE_LABELS } from '../../types/tokenUsage'
import type { TokenUsageFeatureKey } from '../../types/tokenUsage'
import type { LlmUsageFilters, LlmUsageRow, LlmUsageScope } from '../../types/llmUsage'
import { USAGE_WINDOWS } from '../../types/llmUsage'
import {
  compactFilters,
  durationMs,
  formatCost,
  formatDuration,
  formatTokens,
  payloadHasCost,
  queueLabel,
  selectBreakdown,
  wasQueued,
  windowToRange,
} from './llmUsageModel'

/**
 * The LLM usage report — one component, three mounts.
 *
 * WHERE MONEY COMES FROM. Never from a prop, never from a role check here. The
 * component asks the PAYLOAD whether it carries cost (`payloadHasCost`) and
 * renders the column only if it does. The job-scoped endpoint omits cost
 * entirely, so the server is the single authority on who sees margin and this
 * component cannot leak it by mistake.
 *
 * WHAT AN EMPTY LANE MEANS. Only AutoCoder coding and ThemeAI are queued.
 * Everything else — the agent, table analysis, exec reports, generic chat —
 * calls the model directly, and those rows are complete and correct with no
 * lane at all. They are labelled "Direct call", not left blank, so nobody reads
 * a real row as broken data.
 */

export interface LlmUsageReportProps {
  scope: LlmUsageScope
  /** Features this mount is pinned to, e.g. ['autocoder','theme_ai']. */
  lockedFeatures?: string[]
  /** False while behind a hidden tab — a hidden view must not fetch. */
  active?: boolean
  title?: string
  subtitle?: string
  /** Seconds between refetches; 0 (default) means no polling. */
  pollSeconds?: number
  dense?: boolean
}

const featureLabel = (key: string): string =>
  FEATURE_LABELS[key as TokenUsageFeatureKey] ?? key

const formatWhen = (ms: number): string => new Date(ms).toLocaleString()

const LlmUsageReport: React.FC<LlmUsageReportProps> = ({
  scope,
  lockedFeatures,
  active = true,
  title,
  subtitle,
  pollSeconds = 0,
  dense = false,
}) => {
  const isAdmin = scope.kind === 'admin'

  const [windowKey, setWindowKey] = useState('7d')
  const [customer, setCustomer] = useState('')
  const [job, setJob] = useState('')
  const [user, setUser] = useState('')
  const [feature, setFeature] = useState('')
  const [lane, setLane] = useState('')
  // Admin only. The value is the wire form (`label:campaign_id`) rather than a
  // friendly key, so nothing has to translate it on the way out.
  const [groupBy, setGroupBy] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [expanded, setExpanded] = useState<string | null>(null)

  const filters: LlmUsageFilters = useMemo(
    () =>
      compactFilters({
        ...windowToRange(windowKey),
        customer: isAdmin ? customer : undefined,
        job: isAdmin ? job : undefined,
        user_id: user,
        // A mount-level feature lock always wins over the dropdown, which is
        // hidden on those mounts anyway.
        feature: lockedFeatures?.length ? undefined : feature,
        features: lockedFeatures,
        lane,
        group_by: isAdmin ? groupBy : undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      }),
    [windowKey, isAdmin, customer, job, user, feature, lockedFeatures, lane, groupBy, rowsPerPage, page],
  )

  const { rows, summary, loading, isFetching, error, refetch } = useLlmUsage(
    scope,
    filters,
    active,
    pollSeconds,
  )

  const distinctCustomers = useLlmUsageDistinct('customer', isAdmin && active)
  const distinctUsers = useLlmUsageDistinct('user_id', isAdmin && active)
  const distinctLanes = useLlmUsageDistinct('lane', isAdmin && active)

  const showCost = payloadHasCost(rows?.rows, summary)
  const currency = summary?.currency ?? 'USD'

  const resetPage = <T,>(setter: (v: T) => void) => (value: T) => {
    setter(value)
    setPage(0)
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    )
  }

  const summaryTiles: { label: string; value: string }[] = [
    { label: 'Calls', value: (summary?.request_count ?? 0).toLocaleString() },
    { label: 'Tokens', value: formatTokens(summary?.total_tokens) },
    { label: 'Input', value: formatTokens(summary?.total_input_tokens) },
    { label: 'Output', value: formatTokens(summary?.total_output_tokens) },
    { label: 'Cache read', value: formatTokens(summary?.total_cached_tokens) },
    { label: 'Cache write', value: formatTokens(summary?.total_cache_creation_tokens) },
  ]
  if (showCost) {
    summaryTiles.push({ label: 'Est. cost', value: formatCost(summary?.total_cost, currency) })
  }

  const colSpan = showCost ? 9 : 8

  // One breakdown table, whichever dimension was asked for.
  const breakdown = selectBreakdown(summary)

  return (
    <Box>
      {(title || subtitle) && (
        <Box sx={{ mb: 1.5 }}>
          {title && (
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      {/* Filters. Locked scope is rendered as text, not a disabled input: a
          greyed-out dropdown invites clicking and implies the value could
          change, which on a scoped mount it cannot. */}
      <Paper
        elevation={0}
        sx={{ p: 1.5, mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="llm-usage-period-label">Period</InputLabel>
            <Select
              labelId="llm-usage-period-label"
              value={windowKey}
              label="Period"
              onChange={e => resetPage(setWindowKey)(e.target.value)}
            >
              {USAGE_WINDOWS.map(w => (
                <MenuItem key={w.key} value={w.key}>
                  {w.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isAdmin ? (
            <>
              <TextField
                select
                size="small"
                label="Customer"
                sx={{ minWidth: 160 }}
                value={customer}
                onChange={e => resetPage(setCustomer)(e.target.value)}
              >
                <MenuItem value="">All customers</MenuItem>
                {(distinctCustomers.data ?? []).map(c => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="User"
                sx={{ minWidth: 200 }}
                value={user}
                onChange={e => resetPage(setUser)(e.target.value)}
              >
                <MenuItem value="">All users</MenuItem>
                {(distinctUsers.data ?? []).map(u => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Lane"
                sx={{ minWidth: 170 }}
                value={lane}
                onChange={e => resetPage(setLane)(e.target.value)}
              >
                <MenuItem value="">All lanes</MenuItem>
                {(distinctLanes.data ?? []).map(l => (
                  <MenuItem key={l} value={l}>
                    {l}
                  </MenuItem>
                ))}
              </TextField>
              {/* ADMIN ONLY, and enforced server-side rather than here: the
                  job-scoped handler strips a label grouping before it reaches
                  the store, because enumerating a customer's campaign ids back
                  at them is disclosure. This control is the UI half of a rule
                  that does not depend on the UI. */}
              <TextField
                select
                size="small"
                label="Group by"
                sx={{ minWidth: 170 }}
                value={groupBy}
                data-testid="llm-usage-group-by"
                onChange={e => resetPage(setGroupBy)(e.target.value)}
              >
                <MenuItem value="">No breakdown</MenuItem>
                <MenuItem value="user_id">User</MenuItem>
                <MenuItem value="label:campaign_id">Campaign</MenuItem>
              </TextField>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {scope.customer} / {scope.job}
            </Typography>
          )}

          {!lockedFeatures?.length && (
            <TextField
              select
              size="small"
              label="Feature"
              sx={{ minWidth: 180 }}
              value={feature}
              onChange={e => resetPage(setFeature)(e.target.value)}
            >
              <MenuItem value="">All features</MenuItem>
              {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Refresh now">
            <span>
              <IconButton
                color="info"
                onClick={refetch}
                disabled={isFetching}
                aria-label="Refresh LLM usage"
              >
                {isFetching ? <CircularProgress size={20} color="info" /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Totals for the whole filtered set, not for the page being looked at. */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          mb: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {summaryTiles.map(tile => (
          <Box
            key={tile.label}
            sx={{ px: 2.5, py: 1.25, borderRight: '1px solid', borderColor: 'divider' }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {tile.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {tile.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* The grouped breakdown, above the rows it summarises.

          THE UNATTRIBUTED BUCKET IS SHOWN, NOT DROPPED. The server emits a
          `''` dimension for rows whose labels carry no such key — pre-label
          spend and any batch nobody grouped — and likewise for calls billed
          to no user. That is real money, and a report that silently omitted
          it would not add up to its own total. */}
      {breakdown && breakdown.rows.length > 0 && (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', mb: 1.5 }}
          data-testid={breakdown.testId}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{breakdown.title}</TableCell>
                <TableCell align="right">Calls</TableCell>
                <TableCell align="right">Tokens</TableCell>
                {showCost && <TableCell align="right">Est. cost</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {breakdown.rows.map(dim => (
                <TableRow key={dim.dimension || '(unattributed)'} hover>
                  <TableCell>
                    {dim.dimension ? (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {dim.dimension}
                      </Typography>
                    ) : (
                      <Tooltip title={breakdown.unattributedHint}>
                        <Typography variant="body2" color="text.secondary">
                          (unattributed)
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">{dim.request_count.toLocaleString()}</TableCell>
                  <TableCell align="right">{formatTokens(dim.total_tokens)}</TableCell>
                  {showCost && (
                    <TableCell align="right">{formatCost(dim.cost, currency)}</TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size={dense ? 'small' : 'medium'} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>When</TableCell>
              <TableCell>Feature</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Lane</TableCell>
              <TableCell>State</TableCell>
              <TableCell align="right">Tokens</TableCell>
              <TableCell align="right">Took</TableCell>
              {showCost && <TableCell align="right">Est. cost</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !rows ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
                  <CircularProgress color="info" size={24} />
                </TableCell>
              </TableRow>
            ) : !rows?.rows?.length ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No model calls in this period.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.rows.map(r => (
                <UsageRow
                  key={r.id}
                  row={r}
                  showCost={showCost}
                  currency={currency}
                  open={expanded === r.id}
                  onToggle={() => setExpanded(expanded === r.id ? null : r.id)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={rows?.total_count ?? 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_e, next) => setPage(next)}
        onRowsPerPageChange={e => {
          setRowsPerPage(parseInt(e.target.value, 10))
          setPage(0)
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  )
}

const UsageRow: React.FC<{
  row: LlmUsageRow
  showCost: boolean
  currency: string
  open: boolean
  onToggle: () => void
}> = ({ row, showCost, currency, open, onToggle }) => {
  const queued = wasQueued(row)
  const colSpan = showCost ? 9 : 8

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
        <TableCell>
          <IconButton
            size="small"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={
              open
                ? `Hide detail for ${formatWhen(row.created_at)} ${featureLabel(row.feature_key)}`
                : `Show detail for ${formatWhen(row.created_at)} ${featureLabel(row.feature_key)}`
            }
          >
            {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatWhen(row.created_at)}</TableCell>
        <TableCell>{featureLabel(row.feature_key)}</TableCell>
        <TableCell>{row.model || '—'}</TableCell>
        <TableCell>
          {queued ? (
            row.lane
          ) : (
            // Em dash, not blank: this call is complete and correct, it simply
            // never went near a lane.
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            variant={queued ? 'filled' : 'outlined'}
            color={row.state === 'failed' ? 'error' : queued ? 'info' : 'default'}
            label={queueLabel(row)}
          />
        </TableCell>
        <TableCell align="right">{formatTokens(row.total_tokens)}</TableCell>
        <TableCell align="right">{formatDuration(durationMs(row))}</TableCell>
        {showCost && <TableCell align="right">{formatCost(row.cost, currency)}</TableCell>}
      </TableRow>

      <TableRow>
        <TableCell colSpan={colSpan} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <Detail label="Scope" value={`${row.customer}${row.job ? ` / ${row.job}` : ''}`} />
                <Detail label="Billed to" value={row.user_id || '—'} />
                <Detail label="Input" value={row.input_tokens.toLocaleString()} />
                <Detail label="Output" value={row.output_tokens.toLocaleString()} />
                <Detail label="Cache read" value={row.cached_tokens.toLocaleString()} />
                <Detail label="Cache write" value={row.cache_creation_tokens.toLocaleString()} />
                {row.reasoning_tokens > 0 && (
                  <Detail label="Reasoning" value={row.reasoning_tokens.toLocaleString()} />
                )}
                {queued && (
                  <>
                    <Detail label="Request type" value={row.request_type ?? '—'} />
                    <Detail
                      label="Estimate vs actual"
                      value={
                        row.estimated_tokens
                          ? `${formatTokens(row.estimated_tokens)} → ${formatTokens(row.total_tokens)}`
                          : '—'
                      }
                    />
                    <Detail label="Attempts" value={String(row.attempt_count ?? 1)} />
                  </>
                )}
                {row.client_ref && <Detail label="Part of" value={row.client_ref} mono />}
              </Stack>
              {row.request_error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {row.request_error}
                </Alert>
              )}
              {!queued && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  This call was made directly rather than through the model queue, so it has no lane
                  or queue position. Only AutoCoder coding and Theme AI are queued.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

const Detail: React.FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : undefined }}>
      {value}
    </Typography>
  </Box>
)

export default LlmUsageReport
